import {
	Card,
	CardBody,
	CardHeader,
	CardHeading,
	FormikWithRedirectForm,
	Icon,
	Loading,
	SubmitButton,
	isSuccess,
	useHasPermission,
	useToasts,
} from "@dgs/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PermissionName } from "../../../shared/types/permission";
import { UserOnboardingResource } from "../../../shared/types/userOnboarding";
import { userOnboardingService } from "../shared/api/userOnboardingService";
import { RoleSingleSelect } from "../shared/select/RoleSingleSelect";
import { TenantSingleSelect } from "../shared/select/TenantSelect";

const Actions = styled.div`
	display: flex;
	justify-content: end;
`;

const StyledCard = styled(Card)`
	margin: ${({ theme }) => theme.spacing(8)};
`;

const InfoIcon = styled(Icon)`
	height: 1.5rem;
	width: 1.5rem;
`;

const Info = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(2)};
	margin: ${({ theme }) => theme.spacing(5)} 0;
`;

const useValidate = () => {
	const { t } = useTranslation();

	return (values: UserOnboardingResource) => {
		const errors: { [K in keyof UserOnboardingResource]?: string } = {};

		if (!values.defaultTenant) {
			errors.defaultTenant = t("Required field");
		}
		return errors;
	};
};

export const UserOnboardingPage: FC = () => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const validate = useValidate();

	const { data: initialValues, isLoading } = useQuery({
		queryKey: userOnboardingService.showKey,
		queryFn: () => userOnboardingService.show().then((r) => r.data.data),
	});

	const handleSubmit = async (values: UserOnboardingResource) => {
		const result = await userOnboardingService.update({
			defaultTenantId: values.defaultTenant.id,
			defaultRoleId: values.defaultRole?.id ?? null,
		});

		if (isSuccess(result)) {
			showToast({ title: t("User onboarding"), type: "success", body: t("User onboarding saved.") });
			void queryClient.invalidateQueries({ queryKey: userOnboardingService.showKey });
			return true;
		}

		return false;
	};

	const canUpdate = useHasPermission<PermissionName>(["userOnboarding.update"]);

	return (
		<StyledCard>
			<CardHeader>
				<CardHeading>{t("User onboarding")}</CardHeading>
			</CardHeader>
			<CardBody>
				{isLoading || initialValues === undefined ? (
					<Loading />
				) : (
					<>
						<Info>
							<InfoIcon $icon="info" />
							{t("All new users automatically receive the selected default role.")}
						</Info>
						<FormikWithRedirectForm onSubmit={handleSubmit} initialValues={initialValues} validate={validate}>
							<TenantSingleSelect
								label={t("Default tenant")}
								name="defaultTenant"
								required={true}
								disabled={!canUpdate}
							/>
							<RoleSingleSelect label={t("Default role")} name="defaultRole" clearable={true} disabled={!canUpdate} />
							<Actions>
								<SubmitButton disabled={!canUpdate}>{t("Save")}</SubmitButton>
							</Actions>
						</FormikWithRedirectForm>
					</>
				)}
			</CardBody>
		</StyledCard>
	);
};
